import React, { useState, useEffect } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faVideo, faVideoSlash, faMicrophoneSlash, faClosedCaptioning } from "@fortawesome/free-solid-svg-icons";
import { Box, Stack, Button, Tooltip, CircularProgress } from '@mui/material';
import "./Footer.css";
import { getChatHist } from '../chat_history/speech-to-text.jsx';
import posthog from "posthog-js";


const Footer = (props) => {
  const [mic, setMic] = useState(props.isMicOn);
  const [video, setVideo] = useState(props.isVideoOn);
  const [cc, setCc] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState(props.candidateEmail);
  const [jobId, setJobId] = useState(null);
  const [quickMockJobId, setQuickMockJobId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const joinTime = props.joinTime;

  useEffect(() => {
      // 2b
      const queryParams = window.location.search.slice(1); 
      const paramsArray = queryParams.split('&'); 
  
      const jobParam = paramsArray.find(param => param.startsWith('job='));

      if (jobParam) { 
        const jobId = jobParam.split('=')[1]; 
        setJobId(jobId);
      } else if (props.quickMockJobId) {
        setQuickMockJobId(props.quickMockJobId);
      }
  }, []);

  // Update email when props change
  useEffect(() => {
    setCandidateEmail(props.candidateEmail);
  }, [props.candidateEmail]);

  const micClick = () => {
    props.onMicClick(!mic);
    setMic(!mic);
  };

  const videoClick = () => {
    props.onVideoClick(!video);
    setVideo(!video);
  };

  const ccClick = () => {
    props.onccClick(!cc);
    setCc(!cc);
  };

  const handleEndButtonClick = async () => {
    props.setEndButtonClicked(true);
    posthog.capture('interview_click_end_button', {});

    if (joinTime) {
      const durationMs = Date.now() - joinTime;
      const durationMinutes = durationMs / 1000 / 60;
      posthog.capture('interview_duration', {duration_minite: durationMinutes});
    }

    // Check if this is Question Bank practice mode
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const questionId = urlParams.get('questionId');
    
    if ((mode === 'practice' || mode === 'followup') && (questionId || urlParams.get('originalQuestionId'))) {
      console.log('[INFO] Question Bank practice mode detected, handling QB feedback');
      
      // For Question Bank practice, we need to collect the user's response and generate feedback
      const fullChatHist = JSON.stringify(getChatHist(), null, 2).replace(/\\n/g, '\n');
      
      // Extract user's response from chat history
      const chatHistory = getChatHist();
      const userResponses = chatHistory.filter(msg => msg.role === 'user' && msg.content !== '...');
      const userResponse = userResponses.map(msg => msg.content).join('\n\n');
      
      if (!userResponse.trim()) {
        alert('Please provide a response before ending the practice session.');
        props.setEndButtonClicked(false);
        return;
      }
      
      try {
        // Generate session ID
        const sessionId = `qb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Calculate practice time
        const practiceTime = joinTime ? 
          `${Math.floor((Date.now() - joinTime) / 60000)}m ${Math.floor(((Date.now() - joinTime) % 60000) / 1000)}s` : 
          'Not recorded';
        
        // Call Question Bank feedback API
        const originalQuestionId = urlParams.get('originalQuestionId');
        const finalQuestionId = questionId || originalQuestionId;
        
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/feedback`, {
          questionId: finalQuestionId,
          sessionId,
          userEmail: candidateEmail,
          userResponse,
          practiceTime,
          isFollowup: mode === 'followup'
        });
        
        posthog.capture('qb_practice_completed', {
          questionId,
          sessionId,
          practiceTime,
          responseLength: userResponse.length
        });
        
        // Redirect to Question Bank feedback page
        window.location.href = `/question-bank-feedback?sessionId=${sessionId}`;
        return;
        
      } catch (error) {
        console.error('Error generating Question Bank feedback:', error);
        alert('Failed to generate feedback. Please try again.');
        props.setEndButtonClicked(false);
        return;
      }
    }
    

    // Normal interview logic continues here
    try {
      let fullChatHist;
      
      if (props.agentId) {
        const cloudHistory = await fetchConversationHistory();
        fullChatHist = JSON.stringify(cloudHistory, null, 2);
      } else {
        fullChatHist = JSON.stringify(getChatHist(), null, 2).replace(/\\n/g, '\n');
      }
  
      const apiUrl = process.env.REACT_APP_BACKEND_URL + "/api/openai/postInterview";
      const response = await axios.post(apiUrl, {
        userEmail: candidateEmail,
        message: fullChatHist,
        promptId: props.interviewPromptId,
        interviewQuestion: props.interviewQuestion,
        jobId: jobId,
        quickMockJobId: quickMockJobId,
        round:  props.isB2BInterview ? "hr-round" : props.round,
        agentId: props.agentId,
        mode: mode 
      });
  
      const queryParamsObj = new URLSearchParams({ id: response.data.data });
      if (jobId) queryParamsObj.append('job', jobId);
      if (props.agentId) queryParamsObj.append('agent_id', props.agentId);
      
      //check here
      // window.location.href = `/feedback?${queryParamsObj.toString()}`; 

      if (props.isB2BInterview) {
        const feedbackRes = await axios.post(process.env.REACT_APP_BACKEND_URL + "/api/userData/feedback", {
          feedbackId: response.data.data,
                email: candidateEmail,
                jobId: jobId,
        });
        
        // Process feedback data and submit to B2B backend
        if (feedbackRes.data && feedbackRes.data.data && feedbackRes.data.data.reply) {
          const feedbackData = feedbackRes.data.data.reply;
          
          // Extract scores from feedback content
          const contentSections = feedbackData.content.content || [];
          const scoreSections = {};
          
          contentSections.forEach(section => {
            scoreSections[section.section] = {
              score: section.score !== 'n/a' ? parseInt(section.score) : 0,
              feedback: section.text,
              improvement: section.improve
            };
          });
          
          // Create the object to send to B2B backend
          const b2bSubmission = {
            jobListingId: props.jobListingId,
            candidateDetails: {
              firstName: props.candidateFirstName || 'Anonymous',
              lastName: props.candidateLastName || 'Candidate',
              email: candidateEmail || 'guest@example.com',
              clerkId: props.clerkId
            },
            resume: props.resumeData || '',
            interviewResults: {
              overview: feedbackData.content.overview || '',
              chatHistory: feedbackData.chat_history || fullChatHist,
              sections: scoreSections,
              overallScore: scoreSections['Overall Performance']?.score || 0,
              date: feedbackData.date || new Date().toLocaleDateString()
            }
          };
          
          // Submit to B2B backend
          try {
            const b2bResponse = await axios.post(
              `${process.env.REACT_APP_B2B_BACKEND_URL}/applications/submit-interview`, 
              b2bSubmission
            );
            
            // Redirect to thank you page for B2B interviews
            window.location.href = `/thank-you?job=${jobId}`;
          } catch (b2bError) {
            console.error("Error submitting to B2B backend:", b2bError);
            // Fallback to thank you page even if there's an error
            window.location.href = `/thank-you?job=${jobId}`;
          }
        } else {
          // If no proper feedback data, fallback to regular feedback
          window.location.href = `/feedback?${queryParamsObj.toString()}`;
        }
      } else {
        window.location.href = `/feedback?${queryParamsObj.toString()}`;
      }
  
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };
  
  // Footer.jsx
  const fetchConversationHistory = async () => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    try {
      const apiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;
    
      const listResponse = await axios.get(
        'https://api.elevenlabs.io/v1/convai/conversations',
        {
          headers: { 'xi-api-key': apiKey },
          params: { agent_id: props.agentId }
        }
      );
      const { conversations } = listResponse.data;
      if (!conversations.length) return [];

      const detailResponse = await axios.get(
        `https://api.elevenlabs.io/v1/convai/conversations/${conversations[0].conversation_id}`,
        { headers: { 'xi-api-key': apiKey } }
      );
      return detailResponse.data.transcript.map(msg => ({
        role: msg.role === 'agent' ? 'assistant' : 'user',
        content: msg.message
      }));

    } catch (error) {
      console.error('Error fetching conversation history:', error);
      return [];
    }
  };



  return (
    <>
    {
      props.hasEndButtonClicked? <></> :
    
        <Box className="footer">
          <Stack direction="horizontal">
            <Button
              variant="contained"
              size="large"
              className={!mic ? "footer-icons-active" : "footer-icons"}
              onClick={micClick}
            >
              <Tooltip title={mic ? "Mute" : "Unmute"}>
                <FontAwesomeIcon icon={!mic ? faMicrophoneSlash : faMicrophone} />
              </Tooltip>
            </Button>

            <Button
              variant="contained"
              size="large"
              className={"footer-icons" + (!video ? "-active" : "")}
              onClick={videoClick}
            >
              <Tooltip title={video ? "Stop Video" : "Start Video"}>
                <FontAwesomeIcon icon={!video ? faVideoSlash : faVideo} />
              </Tooltip>
            </Button>

            {
              props.hasUserJoined &&

              <Button
                variant="contained"
                size="large"
                className={"footer-icons" + (cc ? "" : "-inactive")}
                onClick={ccClick}
              >
                <Tooltip title={cc ? "Hide History" : "Show History"}>
                  <FontAwesomeIcon icon={faClosedCaptioning} />
                </Tooltip>
              </Button>
            }
          </Stack>

          {
            props.hasUserJoined &&
            <Button className="normal-text end-button" onClick={handleEndButtonClick}>End</Button>
          }

        </Box>
    }
    </>
  );
};

export default Footer;